import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import type { Publisher, PublisherProperties } from '@opentok/client';
import { defaultPropertiesPublisher } from '../domain/constants';
import { publish, subscribe, unsubscribe } from '../../EventsBus';
import { VideoCallEvents } from '../domain/events';
import { useMediaContext } from '../../Media/providers/MediaContextProvider';
import { MediaDomainEvents } from '../../Media/domain';
import { useSessionStoragePersistedState } from '../../SessionStorageState';

export default function usePublisher() {
	const { selectedMicDevice, selectedVideoDevice, selectedAudioDevice, acceptedMediaPermissions } = useMediaContext();
	const publisher = useRef<Publisher | undefined>(undefined);
	const screenSharePublisher = useRef<Publisher | undefined>(undefined);

	const [publisherPublishing, setPublisherPublishing] = useState(false);
	const [publisherScreenSharing, setPublisherScreenSharing] = useState(false);

	const [muted, setMuted] = useSessionStoragePersistedState('muted', false);
	const [hiddenCam, setHiddenCam] = useSessionStoragePersistedState('hiddenCam', false);

	const publisherReady = useMemo(() => {
		return !!publisher?.current;
	}, [publisher]);

	const createPublisher = useCallback(
		async (target: string) => {
			try {
				const { initPublisher, setAudioOutputDevice } = await import('@opentok/client');
				const publisherProperties = Object.assign(defaultPropertiesPublisher, {
					videoSource: selectedVideoDevice,
					audioSource: selectedMicDevice,
				}) as PublisherProperties;

				publisher.current = initPublisher(target, publisherProperties, (error) => {
					if (error) {
						console.error('Publisher creation error:', error);

						if (error.name === 'OT_UNABLE_TO_CAPTURE_MEDIA' ||
							error.name === 'InvalidStateError' ||
							error.message?.includes('AbortError') ||
							error.message?.includes('InvalidStateError') ||
							error.message?.includes('Unable to capture media')) {
							publish(VideoCallEvents.publisherDevicesInUse, {
								error: error,
								message: 'Devices already in use'
							});
						} else {
							publish(VideoCallEvents.publisherCreationFailed, {
								error: error,
							});
						}
					} else {
						publish(VideoCallEvents.publisherCreated);
					}
				});
				// publisher.current.

				if (selectedAudioDevice) {
					setAudioOutputDevice(selectedAudioDevice)
						.then(() => {})
						.catch((err) => {
							publish(VideoCallEvents.publisherAudioOutputDeviceFailed, {
								error: err,
							});
						});
				}

				publisher.current.publishVideo(!hiddenCam);
				publisher.current.publishAudio(!muted);
				setPublisherPublishing(true);
				publish(VideoCallEvents.publisherConfigured);
			} catch (err) {
				publish(VideoCallEvents.publisherCreationFailed, {
					error: err,
				});
			}
		}, [publisher, selectedAudioDevice, selectedMicDevice, selectedVideoDevice, hiddenCam, muted]);

	const destroyPublisher = useCallback(() => {
		if (!publisher.current) {
			return;
		}

		try {
			publisher.current.destroy();
		} catch (err) {
			console.error('Publisher destroy error:', err);
		}
		publisher.current = undefined;
		setPublisherPublishing(false);
		publish(VideoCallEvents.publisherDestroyed);
	}, [publisherReady, publisher]);


	const changeMicDevice = useCallback((event: CustomEvent<{deviceId: string}>) => {
		if (!publisherReady) {
			return;
		}

		publisher?.current?.setAudioSource(event.detail.deviceId);
	}, [publisherReady]);

	const changeCamDevice = useCallback((event: CustomEvent<{deviceId: string}>) => {

		publisher?.current?.setVideoSource(event.detail.deviceId);
	}, [publisherReady]);

	const changeAudioOutputDevice = useCallback(async (event: CustomEvent<{deviceId: string}>) => {
		if (!publisherReady) {
			return;
		}

		const { setAudioOutputDevice } = await import('@opentok/client');
		setAudioOutputDevice(event.detail.deviceId)
			.then(() => {})
			.catch((err) => {
				publish(VideoCallEvents.publisherAudioOutputDeviceFailed, {
					error: err,
				});
			});
	}, [publisherReady]);

	const cycleVideo = useCallback(() => {
		publisher?.current?.cycleVideo();
	}, [publisher]);

	const startScreenSharing = useCallback(
		async (target: string) => {
			try {
				const { initPublisher } = await import('@opentok/client');
				const properties = {
					insertMode: 'append',
					fitMode: 'contain',
					width: '100%',
					height: '100%',
					showControls: false,
					publishAudio: false,
					style: { buttonDisplayMode: 'off' },
					videoSource: 'screen'
				} as PublisherProperties;

				screenSharePublisher.current = initPublisher(target, properties, (error) => {
					if (error) {
						publish(VideoCallEvents.publisherScreenSharingStartFailed, {
							error: error,
						});
						return;
					}

					setPublisherScreenSharing(true);
					publish(VideoCallEvents.publisherScreenSharingStarted, {
						publisher: screenSharePublisher.current,
					});
				});
			} catch (err) {
				publish(VideoCallEvents.publisherScreenSharingStartFailed, {
					error: err,
				});
			}
		}, [screenSharePublisher]);

	const stopScreenSharing = useCallback(() => {
		if (!screenSharePublisher.current) {
			return;
		}
		publish(VideoCallEvents.publisherScreenSharingStopped, {
			publisher: screenSharePublisher.current,
		});

		try {
			screenSharePublisher.current.destroy();
		} catch (err) {
			console.error('Screen share publisher destroy error:', err);
		}
		screenSharePublisher.current = undefined;
		setPublisherScreenSharing(false);
	}, [screenSharePublisher]);

	const toggleMute = useCallback(() => {
		if (!publisher) {
			return;
		}
		publish(muted ? VideoCallEvents.unmute : VideoCallEvents.mute);
		publisher?.current?.publishAudio(muted);
		setMuted(!muted);
	}, [muted, publisher]);

	const toggleCamVisibility = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
		if (!publisher) {
			return;
		}
		e.stopPropagation();
		publisher.current?.publishVideo(hiddenCam, (err)=> {
			if (err) {
				publish(VideoCallEvents.toggleCamVisibilityFailed, {
					error: err,
				});
				return;
			}

			publish(hiddenCam ? VideoCallEvents.showCam : VideoCallEvents.hideCam);
		});
		setHiddenCam(!hiddenCam);
	}, [hiddenCam, publisher]);

	const hideOptionDisabled = useMemo(() => {
		return hiddenCam || !acceptedMediaPermissions;
	}, [hiddenCam, acceptedMediaPermissions]);

	const muteOptionDisabled = useMemo(() => {
		return muted || !acceptedMediaPermissions;
	}, [muted, acceptedMediaPermissions]);

	useEffect(() => {
		subscribe(MediaDomainEvents.changeMicDevice, changeMicDevice);
		subscribe(MediaDomainEvents.changeCamDevice, changeCamDevice);
		subscribe(MediaDomainEvents.changeAudioOutputDevice, changeAudioOutputDevice);

		return () => {
			unsubscribe(MediaDomainEvents.changeMicDevice, changeMicDevice);
			unsubscribe(MediaDomainEvents.changeCamDevice, changeCamDevice);
			unsubscribe(MediaDomainEvents.changeAudioOutputDevice, changeAudioOutputDevice);
		};
	}, [publisher, publisherReady]);

	useEffect(() => {
		return () => {
			destroyPublisher();
			stopScreenSharing();
		};
	}, []);

	return {
		publisher,
		screenSharePublisher,
		publisherScreenSharing,
		publisherPublishing,
		hideOptionDisabled,
		muteOptionDisabled,
		hiddenCam,
		muted,
		createPublisher,
		cycleVideo,
		startScreenSharing,
		stopScreenSharing,
		toggleMute,
		toggleCamVisibility,
		destroyPublisher,
	};
}
